<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  category: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();

const form = ref({
  name: '',
  code: '',
  description: '',
  color: '#6366f1',
  icon: 'folder',
  active: true,
});

const saving = ref(false);
const isEditing = computed(() => !!props.category?.id);

const colors = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
];

onMounted(() => {
  if (props.category) {
    form.value = { ...props.category };
  }
});

const handleSubmit = async () => {
  saving.value = true;

  try {
    if (isEditing.value) {
      await TicketCategoriesAPI.update(props.category.id, form.value);
      useAlert(t('TICKET_CATEGORIES.UPDATE_SUCCESS'));
    } else {
      await TicketCategoriesAPI.create(form.value);
      useAlert(t('TICKET_CATEGORIES.CREATE_SUCCESS'));
    }
    emit('saved');
  } catch (error) {
    const errors = error.response?.data?.errors || [
      t('TICKET_CATEGORIES.SAVE_ERROR'),
    ];
    useAlert(errors.join(', '));
  } finally {
    saving.value = false;
  }
};

const autoGenerateCode = () => {
  if (!form.value.code && form.value.name) {
    form.value.code = form.value.name
      .substring(0, 3)
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
};
</script>

<template>
  <div class="flex flex-col w-full max-w-lg">
    <woot-modal-header
      :header-title="
        isEditing
          ? $t('TICKET_CATEGORIES.EDIT')
          : $t('TICKET_CATEGORIES.NEW')
      "
    />

    <form class="flex flex-col gap-4 p-6" @submit.prevent="handleSubmit">
      <!-- Name -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_CATEGORIES.FORM.NAME') }} *
        </label>
        <input
          v-model="form.name"
          type="text"
          required
          maxlength="100"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          @blur="autoGenerateCode"
        />
      </div>

      <!-- Code -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_CATEGORIES.FORM.CODE') }} *
        </label>
        <input
          v-model="form.code"
          type="text"
          required
          maxlength="5"
          pattern="[A-Za-z0-9]+"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak font-mono uppercase focus:border-woot-500 focus:outline-none"
          :placeholder="$t('TICKET_CATEGORIES.FORM.CODE_PLACEHOLDER')"
        />
        <span class="text-xs text-n-slate-10">
          {{ $t('TICKET_CATEGORIES.FORM.CODE_HELP') }}
        </span>
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_CATEGORIES.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="form.description"
          rows="2"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        />
      </div>

      <!-- Color -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_CATEGORIES.FORM.COLOR') }}
        </label>
        <div class="flex gap-2">
          <button
            v-for="color in colors"
            :key="color"
            type="button"
            class="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
            :class="
              form.color === color ? 'border-n-slate-12' : 'border-transparent'
            "
            :style="{ backgroundColor: color }"
            @click="form.color = color"
          />
        </div>
      </div>

      <!-- Active -->
      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="form.active" type="checkbox" class="rounded" />
        <span class="text-sm text-n-slate-12">
          {{ $t('TICKET_CATEGORIES.FORM.ACTIVE') }}
        </span>
      </label>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4">
        <Button slate faded :label="$t('TICKET_CATEGORIES.FORM.CANCEL')" @click="emit('cancel')" />
        <Button
          type="submit"
          :is-loading="saving"
          :label="isEditing ? $t('TICKET_CATEGORIES.FORM.UPDATE') : $t('TICKET_CATEGORIES.FORM.CREATE')"
        />
      </div>
    </form>
  </div>
</template>
